import appRoutes from "../../routes/app.routes";

export const NAV_LINKS = [
  { label: "Video Profiles", href: "#video-profiles" },
  { label: "Jobs", href: "#jobs" },
  { label: "For Employers", href: "#employer-section" },
];

export const KEYWORDS = ["BE SEEN", "BE HEARD", "BE HIRED"];

export const HERO_FLOAT_CARDS = [
  { id: 1, dot: "default" as const, text: "Recruiter viewed your profile" },
  { id: 2, dot: "gold" as const, text: "New opportunity", strong: "$145K–$175K" },
  { id: 3, dot: "pink" as const, text: "4 new matches" },
  { id: 4, dot: "default" as const, text: "Senior Product Designer · Remote" },
];

export const PATH_CARDS = [
  {
    id: "talent",
    tag: "For Talent",
    title: "Find work where you belong",
    description:
      "Create your profile, tell your story and connect with companies looking for people like you.",
    mockupImage: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&h=400&fit=crop",
    mockupAlt: "Talent Profile Mockup",
    ctaLabel: "Explore opportunities",
    ctaTo: appRoutes.candidateJobSearch.path,
  },
  {
    id: "employer",
    tag: "For Employers",
    title: "Meet talent worth knowing",
    description:
      "Go beyond keywords and discover qualified professionals through experience, skills and authentic profiles.",
    mockupImage: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&h=400&fit=crop",
    mockupAlt: "Employer Dashboard Mockup",
    ctaLabel: "Start hiring",
    ctaTo: appRoutes.salesInquiry.generatePath(),
  },
];

// Demo job cards matching the design mockup. Fictional companies —
// intentionally not real employHER client names.
export const JOB_TABS = ["For You", "Remote", "Technology", "Marketing", "Operations", "More"];

export const JOBS = [
  {
    company: "OpenArt AI",
    logo: "https://ui-avatars.com/api/?name=OpenArt+AI&background=0D8ABC&color=fff&size=32&rounded=true",
    title: "Growth Engineer",
    meta: "San Francisco, CA · Hybrid",
    salary: "$160K–$210K",
    tags: ["AI", "Growth", "Engineering"],
  },
  {
    company: "Nova Health",
    logo: "https://ui-avatars.com/api/?name=Nova+Health&background=7E57C2&color=fff&size=32&rounded=true",
    title: "Product Marketing Lead",
    meta: "Remote · US",
    salary: "$140K–$180K",
    tags: ["Marketing", "Health", "Product"],
  },
  {
    company: "Vault Finance",
    logo: "https://ui-avatars.com/api/?name=Vault+Finance&background=2E7D32&color=fff&size=32&rounded=true",
    title: "Senior Frontend Engineer",
    meta: "New York, NY · Remote",
    salary: "$170K–$220K",
    tags: ["React", "TypeScript", "Fintech"],
  },
  {
    company: "EcoPulse",
    logo: "https://ui-avatars.com/api/?name=EcoPulse&background=FF7043&color=fff&size=32&rounded=true",
    title: "Operations Manager",
    meta: "Austin, TX · On-site",
    salary: "$120K–$155K",
    tags: ["Operations", "Sustainability"],
  },
];

// `icon` keys are mapped to MUI icon components in Suhavi.tsx (SUHAVI_MODULE_ICONS)
// rather than storing JSX here, so this file stays plain data.
export const SUHAVI_MODULES = [
  {
    icon: "ask",
    title: "Ask Suhavi",
    description: "Conversational AI that extracts achievements from your chats and builds your profile automatically.",
    tags: ["Chat", "Fact Extract"],
  },
  {
    icon: "resume",
    title: "Resume Builder",
    description: "Paste a job description and watch Suhavi tailor your resume with ATS-optimized keywords instantly.",
    tags: ["ATS-Compliant", "Inline Edit"],
  },
  {
    icon: "autoApply",
    title: "Auto-Apply Agent",
    description: "Browser automation that fills out repetitive forms and handles OTP verifications on your behalf.",
    tags: ["Automation", "OTP Bypass"],
  },
  {
    icon: "voice",
    title: "Aria Voice Coach",
    description: "Mock interviews with real-time voice analysis, STAR evaluation, and webcam posture checks.",
    tags: ["Voice AI", "Posture Check"],
  },
  {
    icon: "tracker",
    title: "Job Tracker CRM",
    description: "A Kanban board that syncs interviews to your calendar and reminds you to follow up.",
    tags: ["Kanban", "Calendar Sync"],
  },
  {
    icon: "match",
    title: "Smart Match Scoring",
    description: "Multi-channel job discovery with a composite score for skills, experience, domain, and salary.",
    tags: ["40% Skills", "30% Exp"],
  },
];

export const VIDEO_CAROUSEL = [
  {
    name: "Sarah K.",
    role: "Product Manager",
    quote: "I finally felt seen.",
    photo: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&h=1000&fit=crop",
  },
  {
    name: "Amara O.",
    role: "Senior Designer",
    quote: "My story finally mattered.",
    photo: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&h=1000&fit=crop",
  },
  {
    name: "Varsha K.",
    role: "Product Designer",
    quote: "I found my dream role.",
    photo: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=1000&fit=crop",
  },
  {
    name: "Aisha L.",
    role: "Marketing Lead",
    quote: "I was seen for who I am.",
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=1000&fit=crop",
  },
];

export const VIDEO_STEPS = [
  { num: "01", label: "Tell your story" },
  { num: "02", label: "Highlight what makes you different" },
  { num: "03", label: "Let employers see the person behind the resume" },
];

export const HOW_IT_WORKS_STEPS = [
  { title: "Build your profile", description: "Skills, experience and what you're looking for." },
  { title: "Tell your story", description: "Create a short video introduction." },
  { title: "Discover better matches", description: "Explore jobs relevant to you." },
  { title: "Make real connections", description: "Connect with employers and professionals." },
];

// Fictional demo companies, same set referenced in the JOBS list above —
// not real employHER clients.
export const SOCIAL_PROOF_LOGOS = ["Vault", "Nova", "OpenArt", "EcoPulse", "Crest", "Apex"];

export const SOCIAL_PROOF_STATS = [
  { number: "12", suffix: "K+", label: "Professionals" },
  { number: "340", suffix: "+", label: "Employers" },
  { number: "1,8", suffix: "K+", label: "Opportunities" },
  { number: "18", suffix: "", label: "Industries" },
];

// All five are inert labels (not links to other destination pages) — the
// "Career Resources" anchor to #resources was removed per user feedback:
// jumping to a section further down this same page read as confusing next
// to four otherwise-inert items that look like distinct destinations.
export const DIVERSITY_LINKS = [
  { label: "Women in Tech", href: undefined },
  { label: "Return to Work", href: undefined },
  { label: "Inclusive Employers", href: undefined },
  { label: "LGBTQ+ Careers", href: undefined },
  { label: "Career Resources", href: undefined },
];

// All five are women, per feedback that the mosaic (employHER is a
// women-focused platform) shouldn't include male stock photos — two of
// the original five did and were swapped out.
export const MOSAIC_IMAGES = [
  { src: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=600&fit=crop", alt: "Black woman in tech" },
  { src: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop", alt: "Professional woman" },
  { src: "https://images.unsplash.com/photo-1573164713988-8665fc963095?w=400&h=400&fit=crop", alt: "Woman in tech, data center" },
  { src: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop", alt: "Woman of color" },
  { src: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=400&fit=crop", alt: "Professional woman, headshot" },
];

export const RESOURCE_ITEMS = [
  "Resume & ATS Guide",
  "Interview Preparation",
  "AI Career Hub",
  "Salary & Negotiation",
  "Returning to Work",
];

export const TESTIMONIALS = [
  {
    avatarUrl: "https://i.pravatar.cc/150?img=32",
    quote: "For the first time, I felt like employers could see more than what fit on one page.",
    name: "Sarah M.",
    role: "Software Engineer",
  },
  {
    avatarUrl: "https://i.pravatar.cc/150?img=11",
    quote: "The video profile changed everything. I got three interviews in the first week.",
    name: "James R.",
    role: "Product Manager",
  },
  {
    avatarUrl: "https://i.pravatar.cc/150?img=47",
    quote: "employHER helped me find a role that actually fits my values and skills.",
    name: "Aisha L.",
    role: "Marketing Lead",
  },
  {
    avatarUrl: "https://i.pravatar.cc/150?img=12",
    quote: "As an employer, I found candidates I would have never discovered on other platforms.",
    name: "David T.",
    role: "CTO, OpenArt AI",
  },
];

export const EMPLOYER_TAGS = [
  "Inclusive sourcing",
  "Talent discovery",
  "Employer branding",
  "Recruitment support",
];

export const CANDIDATE_CHIPS = [
  { name: "Maya R.", role: "Product Designer", avatarUrl: "https://i.pravatar.cc/150?img=5" },
  { name: "James K.", role: "Engineer", avatarUrl: "https://i.pravatar.cc/150?img=3" },
  { name: "Aisha L.", role: "Marketing", avatarUrl: "https://i.pravatar.cc/150?img=9" },
  { name: "Carlos G.", role: "Operations", avatarUrl: "https://i.pravatar.cc/150?img=60" },
];

export const FOOTER_LINKS = {
  talent: [
    { label: "Find Jobs", to: appRoutes.candidateJobSearch.path },
    { label: "Create Profile", to: appRoutes.userSignUp.path },
    { label: "Video Profile", to: appRoutes.userSignUp.path },
    { label: "Career Resources", href: "#resources" },
  ],
  employers: [
    { label: "Post a Job", to: appRoutes.salesInquiry.generatePath() },
    { label: "Find Talent", to: appRoutes.salesInquiry.generatePath() },
    { label: "Recruitment Solutions", to: appRoutes.salesInquiry.generatePath() },
    { label: "Employer Branding", to: appRoutes.salesInquiry.generatePath() },
  ],
  // No dedicated content pages exist yet for these — left inert rather
  // than pointed at a fabricated destination.
  resources: ["AI Career Hub", "Resume Guide", "Interview Prep", "Career Advice"],
  company: [
    { label: "About", href: "#hero" },
    { label: "Contact", to: appRoutes.contactUs.path },
    { label: "Privacy", to: appRoutes.privacyPolicy.generatePath() },
    { label: "Terms", to: appRoutes.termsOfService.generatePath() },
  ],
};
